import { GROUP_KIND_ID } from '@/lib/groups/group-kind'
import { SIDES_ELEMENT_KIND_ID, isSidesEligibleShape } from '@/lib/object-effects/sides'
import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'

/**
 * Default offset (pt) applied on any side whose `wrapOffset*` field is unset — used both for what
 * the layout panel displays for an untouched field and for what the wrap geometry
 * (`compute-wrap-obstacles.ts`) actually excludes around the shape. The two must agree: checkpoint
 * decode intentionally leaves an absent saved value as `undefined` (faithful round-trip, see
 * `checkpoint-to-shapes.ts`/`checkpoint-to-text.ts`), so this constant is the ONE place that turns
 * "unset" into a real number — every reader must go through it rather than picking its own
 * fallback, or a shape can show 6pt in the panel while actually excluding 0pt on that side.
 */
export const DEFAULT_WRAP_OFFSET_PT = 6

/**
 * Frontend wrap style -> backend `TextWrap.wrapStyle` enum string (`{NONE, BOX, JMP, SHP}` per the
 * protobuf schema comment). 'boundingBox', 'wrap' (Wrap Shape — contour-based, see
 * `lib/text/shape-contour-wrap.ts`), and 'jump' (skips over the object entirely via a full-width
 * exclusion band — see `jumpBandFromRect` in `lib/text/compute-wrap-obstacles.ts`) all have real
 * layout behavior.
 */
export const WRAP_STYLE_TO_BACKEND: Record<WrapStyle, string> = {
  none: 'NONE',
  jump: 'JMP',
  wrap: 'SHP',
  boundingBox: 'BOX',
}

const BACKEND_TO_WRAP_STYLE: Record<string, WrapStyle> = {
  NONE: 'none',
  JMP: 'jump',
  SHP: 'wrap',
  BOX: 'boundingBox',
}

/** Reads a backend `TextWrap.wrapStyle` string into the frontend's `WrapStyle`, tolerating case
 *  variance and returning `undefined` for empty/unrecognized values (treated as 'none'). */
export function wrapStyleFromBackend(raw: unknown): WrapStyle | undefined {
  if (typeof raw !== 'string' || raw.trim() === '') return undefined

  return BACKEND_TO_WRAP_STYLE[raw.trim().toUpperCase()]
}

/** Shape kinds the backend persists a `TextWrap` for: rectangle, irregular polygon, polyline, and
 *  regular polygon (1/2/9/10). A placed photo is not a separate kind — it's one of these same
 *  shapes carrying a `photoFill`, so it's covered by the same kindId check. Text boxes are handled
 *  separately (`eligibleTextBox` in `layout-panel/components/layout-panel-text-wrap.tsx`), not
 *  gated by kindId at all. */
const WRAP_ELIGIBLE_KIND_IDS = new Set([1, 2, 9, 10])

/** Same eligibility check as `isWrapEligibleShape`, by raw kindId — for callers (e.g. checkpoint
 *  decode) that already have a resolved kindId number rather than a shape-shaped object. */
export function isWrapEligibleKindId(kindId: number): boolean {
  return WRAP_ELIGIBLE_KIND_IDS.has(kindId)
}

/** Resolves a shape's effective kindId the same way `resolveKindId` (stowed-element-builder.ts) /
 *  `effectiveShapeKindId` (rounding.ts) do: explicit `kindId` wins (set on every decoded shape,
 *  and on images at creation time); otherwise inferred from `type`, since a freshly-drawn shape
 *  (never saved) has no `kindId` yet. Duplicated here rather than imported from
 *  `stowed-element-builder.ts` to avoid a circular import (that module already imports from this
 *  one for `WRAP_STYLE_TO_BACKEND`). */
function resolvedKindIdForWrap(shape: { kindId?: number; type: string }): number {
  if (typeof shape.kindId === 'number' && Number.isFinite(shape.kindId)) return shape.kindId
  if (shape.type === 'path') return 9
  if (shape.type === 'square') return 1
  if (isSidesEligibleShape(shape)) return SIDES_ELEMENT_KIND_ID

  return 2
}

export function isWrapEligibleShape(
  shape: { kindId?: number; type: string } | null | undefined
): boolean {
  if (shape == null) return false

  return WRAP_ELIGIBLE_KIND_IDS.has(resolvedKindIdForWrap(shape))
}

/** Same as `isWrapEligibleShape`, plus a group row (kindId 7) acting as a wrap SOURCE via its own
 *  `wrapStyle` — distinct from `isWrapEligibleShape` on purpose: that one gates backend-persisted
 *  per-shape TextWrap eligibility (also used by checkpoint decode), and a group's own TextWrap is
 *  persisted separately via `GroupElementData.textWrap` (see `buildStowedElement`'s group case in
 *  `stowed-element-builder.ts` and the group carve-out in `checkpoint-to-shapes.ts`), not via this
 *  per-shape-kind set. Callers still need the `parentGroupId` gate in `wrap-obstacles.ts` to
 *  exclude grouped children/nested groups from acting as independent sources — this predicate
 *  only answers "is this kind eligible at all". */
export function isWrapSourceEligibleShape(
  shape: { kindId?: number; type: string } | null | undefined
): boolean {
  if (shape == null) return false
  if (shape.kindId === GROUP_KIND_ID) return true

  return isWrapEligibleShape(shape)
}
